const PB_ASSET_URI_PREFIX = 'pb-asset://';
const PB_ASSET_ID_RE = /^[a-f0-9-]+$/i;

function assetUri(id) {
  return `${PB_ASSET_URI_PREFIX}${id}`;
}

function cleanAssetId(value) {
  const id = String(value || '').trim();
  return PB_ASSET_ID_RE.test(id) ? id : '';
}

function setAttrIfDifferent(el, name, value) {
  if (!el || !name || !value) return false;
  if (el.getAttribute(name) === value) return false;
  el.setAttribute(name, value);
  return true;
}

function canonicalizePictureSources(img, uri) {
  const picture = img.closest?.('picture');
  if (!picture) return false;
  let changed = false;
  for (const source of picture.querySelectorAll('source')) {
    changed = setAttrIfDifferent(source, 'srcset', uri) || changed;
  }
  return changed;
}

/**
 * Default HTML parser using the global DOMParser (browser + jsdom-polyfilled
 * environments). Returns the wrapper root element, or null when unavailable.
 * Node-only callers with no global DOMParser (e.g. the agent workspace) inject
 * a parser via the `parseHTML` option so this shared canonicalization is reused
 * verbatim instead of being reimplemented — see bundle-fs.js.
 */
function defaultParseHTML(html) {
  if (typeof DOMParser === 'undefined') return null;
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  return doc.body.firstElementChild;
}

/**
 * Repairs persisted HTML that has an authoritative data-pb-asset marker but a
 * render-time src/srcset value (blob:/data:) or no durable pb-asset:// URI.
 *
 * The data-pb-asset attribute is the durable asset identity; src/srcset are
 * runtime display surfaces and must be canonical before save/export/render.
 *
 * @param {string} html
 * @param {{ parseHTML?: (html: string) => Element|null }} [options]
 */
export function canonicalizeAssetBackedMediaHTML(html, options) {
  if (!html || typeof html !== 'string') return html || '';
  if (!html.includes('data-pb-asset')) return html;

  const parse = options?.parseHTML || defaultParseHTML;
  const root = parse(html);
  if (!root) return html;

  let changed = false;
  for (const el of root.querySelectorAll('[data-pb-asset]')) {
    const id = cleanAssetId(el.getAttribute('data-pb-asset'));
    if (!id) continue;
    const uri = assetUri(id);
    const tag = el.tagName;

    if (tag === 'IMG') {
      changed = setAttrIfDifferent(el, 'src', uri) || changed;
      changed = canonicalizePictureSources(el, uri) || changed;
    } else if (tag === 'SOURCE') {
      changed = setAttrIfDifferent(el, 'srcset', uri) || changed;
    } else if (el.hasAttribute('src')) {
      changed = setAttrIfDifferent(el, 'src', uri) || changed;
    } else if (el.hasAttribute('srcset')) {
      changed = setAttrIfDifferent(el, 'srcset', uri) || changed;
    }
  }

  return changed ? root.innerHTML : html;
}

function canonicalizeSection(section, options) {
  if (!section || typeof section !== 'object') return;
  if (typeof section.html === 'string') {
    section.html = canonicalizeAssetBackedMediaHTML(section.html, options);
  }
  if (typeof section.originalHtml === 'string') {
    section.originalHtml = canonicalizeAssetBackedMediaHTML(section.originalHtml, options);
  }
}

/**
 * @param {object} state
 * @param {{ parseHTML?: (html: string) => Element|null }} [options]
 */
export function canonicalizeAssetBackedMediaInState(state, options) {
  if (!state || typeof state !== 'object') return state;
  for (const page of state.pages || []) {
    for (const section of page?.sections || []) canonicalizeSection(section, options);
  }
  if (state.sharedSections && typeof state.sharedSections === 'object') {
    for (const section of Object.values(state.sharedSections))
      canonicalizeSection(section, options);
  }
  return state;
}
